import { apiRequest } from '@/utils/api-request';

/**
 * `cron` recurs off a Temporal Schedule. `one_shot` fires once from a
 * delayed-start run and is only creatable through the agent tools today —
 * but it shows up in these listings, so every consumer has to handle it.
 */
export type ScheduleType = 'cron' | 'one_shot';

export interface WorkflowSchedule {
  id: string;
  workflow_id: string;
  schedule_type: ScheduleType;
  /** Null on one-shots — they have no Temporal Schedule entity. */
  temporal_schedule_id: string | null;
  /** The pending delayed-start run; null on cron. */
  temporal_workflow_id: string | null;
  name: string;
  /** Null on one-shots. Read `run_at` instead. */
  cron_expression: string | null;
  /** Absolute fire time; set on one-shots only. */
  run_at: string | null;
  timezone: string;
  enabled: boolean;
  input_overrides: Record<string, unknown> | null;
  overlap_policy: string;
  created_by: string | null;
  idempotency_key: string | null;
  next_run_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * One-shots cannot be edited, paused, resumed, or triggered early — there is
 * no recurrence to act on, and the API rejects all four. Delete is the only
 * lifecycle action they support.
 */
export const isOneShot = (schedule: {
  schedule_type?: ScheduleType;
}): boolean => schedule.schedule_type === 'one_shot';

export interface CreateScheduleRequest {
  name: string;
  cron_expression: string;
  timezone?: string;
  input_overrides?: Record<string, unknown>;
  overlap_policy?: string;
  enabled?: boolean;
}

export interface UpdateScheduleRequest {
  name?: string;
  cron_expression?: string;
  timezone?: string;
  input_overrides?: Record<string, unknown>;
  overlap_policy?: string;
  enabled?: boolean;
}

function assertOk(response: { detail?: string; success?: boolean }) {
  if (response.detail || response.success === false) {
    throw new Error(response.detail || 'Request failed');
  }
}

export async function createSchedule(
  orgId: string,
  workflowId: string,
  data: CreateScheduleRequest,
): Promise<WorkflowSchedule | null> {
  const response = await apiRequest<WorkflowSchedule>({
    url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/schedules`,
    method: 'POST',
    target: 'workflow',
    body: data as unknown as Record<string, unknown>,
  });
  assertOk(response);
  return response.data || null;
}

export async function listSchedules(
  orgId: string,
  workflowId: string,
): Promise<WorkflowSchedule[]> {
  const response = await apiRequest<WorkflowSchedule[]>({
    url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/schedules`,
    method: 'GET',
    target: 'workflow',
  });
  assertOk(response);
  return response.data || [];
}

export async function updateSchedule(
  orgId: string,
  workflowId: string,
  scheduleId: string,
  data: UpdateScheduleRequest,
): Promise<WorkflowSchedule | null> {
  const response = await apiRequest<WorkflowSchedule>({
    url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/schedules/${scheduleId}`,
    method: 'PUT',
    target: 'workflow',
    body: data as unknown as Record<string, unknown>,
  });
  assertOk(response);
  return response.data || null;
}

export async function deleteSchedule(
  orgId: string,
  workflowId: string,
  scheduleId: string,
): Promise<void> {
  const response = await apiRequest<{ success?: boolean; detail?: string }>({
    url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/schedules/${scheduleId}`,
    method: 'DELETE',
    target: 'workflow',
  });
  assertOk(response);
}

export async function pauseSchedule(
  orgId: string,
  workflowId: string,
  scheduleId: string,
): Promise<WorkflowSchedule | null> {
  const response = await apiRequest<WorkflowSchedule>({
    url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/schedules/${scheduleId}/pause`,
    method: 'POST',
    target: 'workflow',
  });
  assertOk(response);
  return response.data || null;
}

export async function resumeSchedule(
  orgId: string,
  workflowId: string,
  scheduleId: string,
): Promise<WorkflowSchedule | null> {
  const response = await apiRequest<WorkflowSchedule>({
    url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/schedules/${scheduleId}/resume`,
    method: 'POST',
    target: 'workflow',
  });
  assertOk(response);
  return response.data || null;
}

export async function triggerSchedule(
  orgId: string,
  workflowId: string,
  scheduleId: string,
): Promise<{ success: boolean; message: string }> {
  const response = await apiRequest<{ success: boolean; message: string }>({
    url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/schedules/${scheduleId}/trigger`,
    method: 'POST',
    target: 'workflow',
  });
  assertOk(response);
  return response.data || { success: false, message: 'Failed to trigger' };
}
